/**
 * App compatibility shim.
 *
 * Recreates the `App` class and all globals that were previously in app.ts.
 * Registered on `window.App` so legacy code can continue to call
 * `App.showPopupNotification()`, `App.setMenu()`, etc.
 */

import { initAzureWebPubSub, stopAzureWebPubSub } from '@/services/azure-web-pubsub-service';
import {
  showVersionUpdateDialog,
  newVersionCheck,
  newFrontendVersionAvailableCallback,
  latestFrontendVersionCallback,
  latestBackendVersionCallback,
  resetPeriodicVersionCheckTimer,
} from '@/services/version-service';
import {
  getAuthObject,
  setAuthObject,
  getCurrentUserId,
  getCurrentAccessToken,
  getAvailableModules,
  getAvailableSubmodules,
  removeModuleTypeAndAvailableSubmodules,
  removeAuthObject,
  currentUserHasPermission,
  getOfficerLabel,
} from '@/utils/auth';
import { hasValue, hasStringValue, hasNumericValue } from '@/utils/validation';
import { isInArray, removeObjectFromArrayByProperty } from '@/utils/arrays';
import { AccessAuthorities } from '@/models/access-authorities';

import moment from 'moment';
import { uploadBlockBlob } from '@/services/azure-blob-service';
import { showPendingFeatureIntro } from '@/services/feature-intro/show-feature-intro';
import { setPendingFeatureIntros } from '@/services/feature-intro/pending-intros';
import { SessionCheckResult, recordSessionCheck, noteSessionCheckYes } from '@/services/session-check-telemetry';

// ── ToolbarRef interface ──────────────────────────────────────────

interface ToolbarRef {
  enableItem(itemId: string, enable?: boolean): void;
  resize(): void;
  updateItemText(itemId: string, newText: string): void;
  blinkItem(itemId: string, blink?: boolean): void;
}

// ── CallCardController Interface ─────────────────────────────────

export interface CallCardController {
  // Data properties (formerly on $data)
  CC: any;
  CCCntr: any;
  officers: any[];
  availablePeople: any[];
  people: any[];
  vehicles: any[];
  natureOfCallOptions: any[];
  clientOptions: any[];
  siteOptions: any[];
  dispositionOptions: any[];
  patrolZoneNames: string;
  callAlert: any;
  initialClientId: any;
  initialClientAllSites: any[];

  // UI state properties (formerly direct on shim)
  callCardNotClosed: boolean;
  isEditMode: boolean;
  isEditingClosedCallCard: boolean;
  isOfficerOnCallCard: boolean;
  saveOrUpdateButtonEnabled: boolean;
  pageReady: boolean;
  clockTime: string;
  module: string;
  editModeAllowed: boolean;
  mdtOfficerTimeButtonsEnabled: boolean;
  canEnterMDTEditMode: boolean;
  showPersonModal: boolean;
  showVehicleModal: boolean;
  showClientModal: boolean;
  personModalUuid: string | null;
  vehicleModalUuid: string | null;

  // Methods
  openSopModal(): void;
  openClientModal(clientId?: string): void;
  openMapModal(): void;
  openPersonModal(): void;
  openVehicleModal(): void;
  openConnectionsSearchModal(npsdType: string, callback: Function, gridDataItem: any): void;
  openAddCustomLocationDialog(coordinates?: string): void;
  fnUpdateCallCardClientLink(...args: any[]): void;
  fnUpdateCallCardNatureOfCallSopLink(...args: any[]): void;
  calculateVisibleTabs(): void;
  updateCC(data: any): void;
  sendCADCC(): void;
  editMDTCC(): void;
  forceSaveOfficerEdits(): void;
  closePersonModal(): void;
  closeVehicleModal(): void;
  closeConnectionsSearchModal(): void;
}

// ── App class ─────────────────────────────────────────────────────

export class App {
  constructor() {}

  private static geocoder: any;
  static isMobile() {
    const el = document.getElementById('divIsMobile');
    if (!el) return window.innerWidth <= 991;
    return getComputedStyle(el).display === 'none';
  }

  static isSuper: boolean = false;
  static popupNotification: any;
  static callCardDefaults: any;

  static toolbarCAD: ToolbarRef | null = null;
  static toolbarMDT: ToolbarRef | null = null;
  static toolbarADMIN: ToolbarRef | null = null;
  static toolbarPAYMENT: ToolbarRef | null = null;

  static tabCADDS: any;
  static tabMDTDS: any;
  static tabAdminDS: any;
  static primaryOfficerRowIndex: any;
  static mainMenuItems: any;
  static moduleTypeFromURL: string;
  static geoLocation: any = null;

  static npsdSearchType = "";

  static registrationCode: string | null = null;
  static resetPasswordCode: string | null = null;

  static originalReportResult: any = null;
  static originalPersonResult: any = null;
  static originalBusinessResult: any = null;
  static originalVehicleResult: any = null;
  static originalCGResult: any = null;

  static _currentFrontendVersion: number | null = null;

  static _isMapScreenLoaded: boolean = false;
  static _isMapCenterSet: boolean = false;
  static trackLatLngInterval: any = null;

  static profileImages: any;

  static callCardController: CallCardController | null = null;
  static currentCC: any = null;
  static currentCCOriginal: any = null;
  static currentCCCntr: any = null;
  static currentCCSubscribedId: number | null = null;
  static MouseTrapBoundInputs: any;

  static progressUpload: any = null;
  static blobUploadProgress: any = null;

  static callCardSubmitType: any;

  static isUpdatingPanicButtonBlinkStateOnPageLoad = false;

  static hasMdtModuleEntryBeenConfirmed: boolean = false;

  static customLocationList: any = null;

  static getGeocoder(): any {
    App.loadGeocoder();
    return App.geocoder;
  }

  static loadGeocoder(): void {
    App.geocoder = new (window as any).google.maps.Geocoder();
  }

  /**
   * Feature flag for the Geocodio backend proxy: true routes geocoding through
   * POST /api/{ver}/{role}/geocode instead of google.maps.Geocoder. On for every company except
   * this opt-out set, which pulls a company back to Google without a backend release
   * (`?geocode-proxy=true|false` overrides for QA). Trusted-record lookups apply either way.
   *
   * Emptying the set is not cleanup: a company returned to the proxy without the server-side
   * ladder and fallback enabled for it (`orna.geocoding.ladder.enabled-companies`,
   * `orna.geocoding.fallback.enabled-companies`) gets bare Geocodio, the configuration that put
   * vague pins on 816 and 928's patrol maps.
   */
  private static readonly GEOCODING_PROXY_OPT_OUT_COMPANY_IDS: ReadonlySet<number> = new Set<number>([]);

  static useGeocodingProxy(): boolean {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('geocode-proxy')) {
        return params.get('geocode-proxy') === 'true';
      }
    } catch { /* ignore URL access errors */ }
    try {
      // Lazy import-style read of getCurrentCompanyId via window to avoid a circular import
      // between app-compat and utils/auth at module-evaluation time.
      const getCurrentCompanyId = (window as any).getCurrentCompanyId;
      const companyId = typeof getCurrentCompanyId === 'function' ? getCurrentCompanyId() : null;
      if (companyId != null && App.GEOCODING_PROXY_OPT_OUT_COMPANY_IDS.has(Number(companyId))) {
        return false;
      }
    } catch { /* ignore */ }
    return true;
  }

  static setProgressControl() {
    const el = document.getElementById("uploadProgress") as HTMLProgressElement;
    if (el) {
      el.max = 100;
      el.value = 0;
      App.progressUpload = el;
    }
  }

  static initBlobUploadProgressControl() {
    if (!App.blobUploadProgress) {
      const el = document.getElementById("blobUploadProgress") as HTMLProgressElement;
      if (el) {
        el.max = 100;
        el.value = 0;
        App.blobUploadProgress = el;
      }
    }
  }

  static uploadStarted() {
    if (App.IsNOU(App.progressUpload)) return;
    App.progressUpload.value = 0;
    App.progressUpload.style.display = '';
  }

  static blobUploadStarted() {
    if (!hasValue(App.blobUploadProgress)) return;
    App.blobUploadProgress.value = 0;
    App.blobUploadProgress.style.display = '';
  }

  static uploadCompleted() {
    if (App.IsNOU(App.progressUpload)) return;
    App.progressUpload.style.display = 'none';
  }

  static blobUploadCompleted() {
    if (hasValue(App.blobUploadProgress)) {
      App.blobUploadProgress.style.display = 'none';
      App.setBlobUploadProgress(0);
    }
  }

  static uploadProgress(progressValue: number) {
    if (App.IsNOU(App.progressUpload)) return;
    App.progressUpload.value = progressValue;
  }

  static setBlobUploadProgress(progressValue: number) {
    if (hasValue(App.blobUploadProgress)) {
      App.blobUploadProgress.value = progressValue;
    }
  }

  static clearCustomLocationListCache() {
    App.customLocationList = null;
  }

  static unReadMessagesCnt: number = 0;

  static isLoggedInInterval: any = null;

  static sndFire: any = null;
  static sndPanic: any = null;
  static sndPanic2: any = null;
  static sndCCUpdate: any = null;
  static sndChat: any = null;

  static pauseItemChangeCheck: boolean = false;

  static showPopupNotification(notificationMessage: string, type: string, length: number = 125, minimal: boolean = false): void {
    let maxNotificationMessageLength: number = length;
    if (notificationMessage.length > maxNotificationMessageLength) {
      notificationMessage = notificationMessage.substring(0, maxNotificationMessageLength - 3) + "...";
    }
    (window as any)._kstToastAdd(notificationMessage, type, minimal);
  }

  static showStandardErrorPopupNotification() {
    App.showPopupNotification("An error occurred. Please save, refresh, and try again.", "error");
  }

  static menuCleanup(): void {
    App.toolbarCAD = null;
    App.toolbarMDT = null;
    App.toolbarADMIN = null;
    App.toolbarPAYMENT = null;
  }

  private static _setMenuTimeout: any = null;

  static setMenu(sectionType: string, tab?: string | null) {
    if (this._setMenuTimeout) {
      clearTimeout(this._setMenuTimeout);
    }
    this._setMenuTimeout = setTimeout(() => {
      this.setMenuImmediate(sectionType, tab);
      this._setMenuTimeout = null;
    }, 50);
  }

  static setMenuImmediate(sectionType: string, tab?: string | null) {
    if (sectionType !== "CAD") document.getElementById("vueToolbarCAD")!.style.display = 'none';
    if (sectionType !== "MDT") document.getElementById("vueToolbarMDT")!.style.display = 'none';
    if (sectionType !== "ADMIN") document.getElementById("vueToolbarADMIN")!.style.display = 'none';
    if (sectionType !== "PAYMENT") document.getElementById("vueToolbarPAYMENT")!.style.display = 'none';

    if (sectionType !== "CAD" && sectionType !== "MDT" && sectionType !== "ADMIN" && sectionType !== "PAYMENT") {
      return;
    }

    let submodules: Array<string> = getAvailableSubmodules() ?? [];
    let modules: Array<string> = getAvailableModules() ?? [];

    let isPanicButtonEnabled: boolean = (window as any).isPanicButtonGloballyEnabled();

    let sharedItems: any = {
      type: "splitButton",
      text: " ",
      id: "splitToolbarButton",
      menuButtons: [
        { text: "User Profile", id: "UserProfile", click: this.menuClick, group: sectionType, attributes: { "data-group": sectionType }, overflow: "always", togglable: true },
        { text: "Getting Started", id: "btnGettingStarted", click: this.menuGettingStartedClick, overflow: "always" },
        { text: "Knowledge Center", id: "btnKnowledgeCenter", click: this.menuHelpDeskClick, overflow: "always" },
        { text: "Change Module", id: "btnReturnHome", click: this.menuReturnHomeClick, overflow: "always" },
        { text: "Sign Out", id: "btnLogOut", click: this.menuLogOutClick, overflow: "always" }
      ]
    };
    if (modules.length === 1) {
      removeObjectFromArrayByProperty(sharedItems.menuButtons, "id", "btnReturnHome");
    }
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_USER_PROFILE)) {
      removeObjectFromArrayByProperty(sharedItems.menuButtons, "id", "UserProfile");
    }
    if (isPanicButtonEnabled && (sectionType === "CAD" || sectionType === "MDT")) {
      // keep Getting Started
    } else {
      removeObjectFromArrayByProperty(sharedItems.menuButtons, "id", "btnGettingStarted");
    }

    let cadItems = [
      { type: "button", text: "Panic", id: "InProgressPanic", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Home", id: "Home", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "In Progress", id: "InProgress", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: `${getOfficerLabel()} Status`, id: "OfficerStatus", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "New Call", id: "CallCard", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "System Log", id: "Log", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Map", id: "Map", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Status", id: "StatusUpdate", click: (window as any).StatusUpdate, group: sectionType, togglable: false },
      { type: "button", text: "Notes", id: "Notes", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Advisories", id: "Advisories", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Records Search", id: "Search", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Tours", id: "GuardToursNew", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Case Reports", id: "CaseReports", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Citations", id: "Citations", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Messaging", id: "Messaging", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Activity Reports", id: "ActivityReport", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Company Documents", id: "CompanyDocuments", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Analytics Reports", id: "AnalyticsReport", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Emergency Lookup", id: "EmergencyLookup", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Custom Locations", id: "CustomLocationManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Call Alerts", id: "CallAlertManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Address Review", id: "AddressReview", click: this.menuClick, group: sectionType, togglable: true },
      sharedItems
    ];
    // Address Review is a geocodio-pilot feature: require the proxy flag in addition to the
    // report-approval role so non-pilot companies never see the menu item (keeps the pilot
    // fully reversible by toggling useGeocodingProxy).
    if (!App.useGeocodingProxy() || !isInArray(submodules, AccessAuthorities.ROLE_REPORT_APPROVAL)) removeObjectFromArrayByProperty(cadItems, "id", "AddressReview");
    if (!isInArray(submodules, AccessAuthorities.ROLE_GUARD_TOUR)) removeObjectFromArrayByProperty(cadItems, "id", "GuardToursNew");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ACTIVITY_REPORT)) removeObjectFromArrayByProperty(cadItems, "id", "ActivityReport");
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_COMPANY_DOCUMENTS)) removeObjectFromArrayByProperty(cadItems, "id", "CompanyDocuments");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ANALYTICS_REPORT_PDF)) removeObjectFromArrayByProperty(cadItems, "id", "AnalyticsReport");
    if (!isInArray(submodules, AccessAuthorities.ROLE_EMERGENCY_LOOKUP)) removeObjectFromArrayByProperty(cadItems, "id", "EmergencyLookup");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CASE_REPORT)) removeObjectFromArrayByProperty(cadItems, "id", "CaseReports");
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_MESSAGING)) removeObjectFromArrayByProperty(cadItems, "id", "Messaging");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_VIEW_SECTION)) removeObjectFromArrayByProperty(cadItems, "id", "CustomLocationManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT) && !isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT_VIEW)) removeObjectFromArrayByProperty(cadItems, "id", "CallAlertManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CITATION_VIEW)) removeObjectFromArrayByProperty(cadItems, "id", "Citations");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ORNA_NOTES_READ)) removeObjectFromArrayByProperty(cadItems, "id", "Notes");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ORNA_ADVISORY_READ)) removeObjectFromArrayByProperty(cadItems, "id", "Advisories");
    if (localStorage.getItem('IsStatusUpdateForDispatchersEnabled') !== 'true') removeObjectFromArrayByProperty(cadItems, "id", "StatusUpdate");
    if (isPanicButtonEnabled) {
      removeObjectFromArrayByProperty(cadItems, "id", "Home");
    } else {
      removeObjectFromArrayByProperty(cadItems, "id", "InProgressPanic");
    }

    let mdtItems = [
      { type: "button", text: "Panic", id: "PanicButton", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Home", id: "Home", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Self Initiate", id: "SelfInitiate", click: (window as any).SelfInitiate, group: sectionType, togglable: false },
      { type: "button", text: "Status", id: "StatusUpdate", click: (window as any).StatusUpdate, group: sectionType, togglable: false },
      { type: "button", text: "Tours", id: "GuardToursNew", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "In Progress", id: "InProgress", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: `${getOfficerLabel()} Status`, id: "OfficerStatus", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Map", id: "Map", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Notes", id: "Notes", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Advisories", id: "Advisories", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Records Search", id: "Search", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Case Reports", id: "CaseReports", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Citations", id: "Citations", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Messaging", id: "Messaging", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "System Log", id: "Log", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Activity Reports", id: "ActivityReport", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Company Documents", id: "CompanyDocuments", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Analytics Reports", id: "AnalyticsReport", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Emergency Lookup", id: "EmergencyLookup", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Custom Locations", id: "CustomLocationManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Call Alerts", id: "CallAlertManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Address Review", id: "AddressReview", click: this.menuClick, group: sectionType, togglable: true },
      sharedItems
    ];
    // Pilot-gated alongside the report-approval role — see the CAD list above.
    if (!App.useGeocodingProxy() || !isInArray(submodules, AccessAuthorities.ROLE_REPORT_APPROVAL)) removeObjectFromArrayByProperty(mdtItems, "id", "AddressReview");
    if (!isInArray(submodules, AccessAuthorities.ROLE_MDT_CALL_CARD_SAVE)) removeObjectFromArrayByProperty(mdtItems, "id", "SelfInitiate");
    if (localStorage.getItem('IsStatusUpdateEnabled') !== 'true') removeObjectFromArrayByProperty(mdtItems, "id", "StatusUpdate");
    if (!isInArray(submodules, AccessAuthorities.ROLE_GUARD_TOUR)) removeObjectFromArrayByProperty(mdtItems, "id", "GuardToursNew");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ACTIVITY_REPORT)) removeObjectFromArrayByProperty(mdtItems, "id", "ActivityReport");
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_COMPANY_DOCUMENTS)) removeObjectFromArrayByProperty(mdtItems, "id", "CompanyDocuments");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ANALYTICS_REPORT_PDF)) removeObjectFromArrayByProperty(mdtItems, "id", "AnalyticsReport");
    if (!isInArray(submodules, AccessAuthorities.ROLE_EMERGENCY_LOOKUP)) removeObjectFromArrayByProperty(mdtItems, "id", "EmergencyLookup");
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_MESSAGING)) removeObjectFromArrayByProperty(mdtItems, "id", "Messaging");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_VIEW_SECTION)) removeObjectFromArrayByProperty(mdtItems, "id", "CustomLocationManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT) && !isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT_VIEW)) removeObjectFromArrayByProperty(mdtItems, "id", "CallAlertManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CITATION_VIEW)) removeObjectFromArrayByProperty(mdtItems, "id", "Citations");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ORNA_NOTES_READ)) removeObjectFromArrayByProperty(mdtItems, "id", "Notes");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ORNA_ADVISORY_READ)) removeObjectFromArrayByProperty(mdtItems, "id", "Advisories");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CASE_REPORT)) removeObjectFromArrayByProperty(mdtItems, "id", "CaseReports");
    if (isPanicButtonEnabled) {
      removeObjectFromArrayByProperty(mdtItems, "id", "Home");
    } else {
      removeObjectFromArrayByProperty(mdtItems, "id", "PanicButton");
    }

    let adminItems = [
      { type: "button", text: "Home", id: "Home", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "User Management", id: "UserManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Permissions", id: "Permissions", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Client Management", id: "ClientGroupManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Client Portal", id: "ClientPortal", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Company Profile", id: "CompanyProfile", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Tour Management", id: "TourManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "NOC Management", id: "NOCManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Call Alerts", id: "CallAlertManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Advisory Management", id: "AdvisoryManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Custom Locations", id: "CustomLocationManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Messaging", id: "Messaging", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Vehicle Management", id: "VehicleManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Call Sign Management", id: "CallSignManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Status Management", id: "StatusUpdateManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Custom Forms", id: "CustomFormsManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Company Link", id: "CompanyLink", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Payment Management", id: "PaymentManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Activity Reports", id: "ActivityReport", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Company Documents", id: "CompanyDocuments", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Analytics Reports", id: "AnalyticsReport", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Call Management", id: "CallCardManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Citation Management", id: "CitationManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Case Report Management", id: "CaseReportManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Activity Report Management", id: "ActivityReportManagement", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Company Settings", id: "CompanySettings", click: this.menuClick, group: sectionType, togglable: true },
      { type: "button", text: "Orna Support Attachments", id: "OrnaSupportAttachments", click: this.menuClick, group: sectionType, togglable: true },
      sharedItems
    ];
    if (!isInArray(submodules, AccessAuthorities.ROLE_TOUR)) removeObjectFromArrayByProperty(adminItems, "id", "TourManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ACTIVITY_REPORT)) removeObjectFromArrayByProperty(adminItems, "id", "ActivityReport");
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_COMPANY_DOCUMENTS)) removeObjectFromArrayByProperty(adminItems, "id", "CompanyDocuments");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ANALYTICS_REPORT_PDF)) removeObjectFromArrayByProperty(adminItems, "id", "AnalyticsReport");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CALL_CARD_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "CallCardManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CITATION_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "CitationManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ACTIVITY_REPORT_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "ActivityReportManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CASE_REPORT_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "CaseReportManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_NOC_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "NOCManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_VIEW_MESSAGING)) removeObjectFromArrayByProperty(adminItems, "id", "Messaging");
    if (!isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT) && !isInArray(submodules, AccessAuthorities.ROLE_LOCATION_ALERT_MANAGEMENT_VIEW)) removeObjectFromArrayByProperty(adminItems, "id", "CallAlertManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_ORNA_ADVISORY_TYPE_MANAGE) && !isInArray(submodules, AccessAuthorities.ROLE_ORNA_ADVISORY_AUDIT_LOG)) removeObjectFromArrayByProperty(adminItems, "id", "AdvisoryManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_VIEW_SECTION)) removeObjectFromArrayByProperty(adminItems, "id", "CustomLocationManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_COMPANY_VEHICLE_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "VehicleManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_CALL_SIGN_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "CallSignManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_STATUS_UPDATE_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "StatusUpdateManagement");
    if (!isInArray(submodules, AccessAuthorities.ROLE_FORM_MANAGEMENT)) removeObjectFromArrayByProperty(adminItems, "id", "CustomFormsManagement");

    let submodulesWithAccessRestriction = [
      { role: AccessAuthorities.ROLE_USER_MANAGEMENT, menuId: "UserManagement" },
      { role: AccessAuthorities.ROLE_ACCESS_MANAGEMENT, menuId: "Permissions" },
      { role: AccessAuthorities.ROLE_COMPANY_PROFILE, menuId: "CompanyProfile" },
      { role: AccessAuthorities.ROLE_CLIENT_MANAGEMENT, menuId: "ClientGroupManagement" },
      { role: AccessAuthorities.ROLE_CLIENT_PORTAL_MANAGEMENT, menuId: "ClientPortal" },
      { role: AccessAuthorities.ROLE_PAYMENT_MANAGEMENT, menuId: "PaymentManagement" },
    ];
    for (let submodule of submodulesWithAccessRestriction) {
      if (!isInArray(submodules, submodule.role)) {
        removeObjectFromArrayByProperty(adminItems, "id", submodule.menuId);
      }
    }

    let paymentManagementItems = [
      { type: "button", text: "Payment Management", id: "PaymentManagement", click: this.menuClick, group: sectionType, togglable: true },
      {
        type: "splitButton",
        id: "splitToolbarButton",
        menuButtons: [
          { text: "Knowledge Center", id: "btnKnowledgeCenter", click: this.menuHelpDeskClick, overflow: "always" },
          { text: "Sign Out", id: "btnLogOut", click: this.menuLogOutClick, overflow: "always" }
        ]
      }
    ];

    if (sectionType === "CAD") {
      this.createOrUpdateToolbar("vueToolbarCAD", cadItems, sectionType, tab);
      requestAnimationFrame(() => {
        document.getElementById("vueToolbarCAD")!.style.display = 'block';
        App.toolbarCAD = this.getToolbarRef("vueToolbarCAD");
      });
    } else if (sectionType === "MDT") {
      const activeCallCardId = (window as any).getMDTActiveCallCardId();
      const selfInitiateItem = mdtItems.find((item: any) => item.id === "SelfInitiate");
      if (selfInitiateItem) {
        selfInitiateItem.text = hasNumericValue(activeCallCardId) ? "My Call" : "Self Initiate";
      }
      this.createOrUpdateToolbar("vueToolbarMDT", mdtItems, sectionType, tab);
      requestAnimationFrame(() => {
        document.getElementById("vueToolbarMDT")!.style.display = 'block';
        App.toolbarMDT = this.getToolbarRef("vueToolbarMDT");
      });
    } else if (sectionType === "ADMIN") {
      this.createOrUpdateToolbar("vueToolbarADMIN", adminItems, sectionType, tab);
      requestAnimationFrame(() => {
        document.getElementById("vueToolbarADMIN")!.style.display = 'block';
        App.toolbarADMIN = this.getToolbarRef("vueToolbarADMIN");
      });
    } else if (sectionType === "PAYMENT") {
      this.createOrUpdateToolbar("vueToolbarPAYMENT", paymentManagementItems, sectionType, tab);
      requestAnimationFrame(() => {
        document.getElementById("vueToolbarPAYMENT")!.style.display = 'block';
        App.toolbarPAYMENT = this.getToolbarRef("vueToolbarPAYMENT");
      });
    }

    this.updateToolbarMessageCount();
  }

  static createOrUpdateToolbar(containerId: string, items: any[], sectionType: string, tab?: string | null) {
    const container = document.getElementById(containerId);
    if (!container) return;
    const currentTab = tab ?? this.getParam("tab", window.location.href);

    (window as any).OrnaReact.renderComponent(
      (window as any).OrnaToolbarReact,
      {
        items: items,
        sectionType: sectionType,
        activeTab: currentTab,
      },
      container
    );
  }

  static getToolbarRef(containerId: string): ToolbarRef | null {
    const container = document.getElementById(containerId);
    if (!container) return null;

    const reactRef = (container as any).__ornaToolbarRef;
    if (reactRef) {
      return {
        enableItem: (itemId: string, enable: boolean = true) => {
          if (reactRef.enableItem) reactRef.enableItem(itemId, enable);
        },
        resize: () => {
          if (reactRef.resize) reactRef.resize();
        },
        updateItemText: (itemId: string, newText: string) => {
          if (reactRef.updateItemText) reactRef.updateItemText(itemId, newText);
        },
        blinkItem: (itemId: string, blink: boolean = true) => {
          if (reactRef.blinkItem) reactRef.blinkItem(itemId, blink);
        }
      };
    }

    return null;
  }

  static updateToolbarMessageCount() {
    window.dispatchEvent(new CustomEvent('toolbar-update-message-count', {
      detail: { count: App.unReadMessagesCnt }
    }));
  }

  static menuClick(e: any) {
    const groupEl = (e.target as HTMLElement).closest('[data-group]') as HTMLElement;
    let section = groupEl ? groupEl.getAttribute("data-group") : null;

    if (!section) {
      section = App.moduleTypeFromURL?.toUpperCase() || "";
    }

    const btnEl = (e.target as HTMLElement).closest('button') as HTMLElement;
    const menuId = e.target.id || (btnEl ? btnEl.id : '');

    if (menuId === "PanicButton") {
      (window as any).MDT_PanicButton();
      return;
    }

    let multiURLSegment: string;
    if (menuId === "UserProfile" || menuId === "Map" || menuId === "Search" || menuId === "Messaging" || menuId === "Notes"
        || menuId === "ActivityReport" || menuId === "CallCard" || menuId === "TourManagement" || menuId === "Log"
        || menuId === "PaymentManagement" || menuId === "GuardToursNew" || menuId === "CompanyDocuments"
        || menuId === "AnalyticsReport" || menuId === "CallCardManagement" || menuId === "CitationManagement"
        || menuId === "ActivityReportManagement" || menuId === "CaseReportManagement" || menuId === "EmergencyLookup"
        || menuId === "InProgressActiveCardsFullScreen" || menuId === "InProgressPanic" || menuId === "CaseReports"
        || menuId === "CallAlertManagement" || menuId === "AdvisoryManagement" || menuId === "Advisories" || menuId === "CustomLocationManagement" || menuId === "VehicleManagement"
        || menuId === "CallSignManagement" || menuId === "StatusUpdateManagement" || menuId === "CompanySettings" || menuId === "OrnaSupportAttachments"
        || menuId === "CustomFormsManagement" || menuId === "Citations") {
      multiURLSegment = "&multiModule=true";
    } else {
      multiURLSegment = "";
    }

    App.updateToolbarMessageCount();

    (window as any).OrnaReact.navigate.to({ section: section, tab: menuId, fn: `${section}_${menuId}`, multiModule: multiURLSegment !== "" });
  }

  static removePubNub() {
    stopAzureWebPubSub();
  }

  static menuLogOutClick(_e: any) {
    (window as any).api.logout((_result: any) => {
      App.signOut();
    }, () => {
      App.showPopupNotification("Error logging out.", "error");
      App.signOut();
    });
  }

  static menuReturnHomeClick(_e: any) {
    localStorage.removeItem('LastRoute');
    removeModuleTypeAndAvailableSubmodules();
    (window as any).OrnaReact.navigate.toHome();
  }

  static goToHelpDesk(type: any) {
    const HelpDeskType = (window as any).HelpDeskType;
    let link: string | null = null;
    if (type === HelpDeskType.Home) {
      link = "https://support.kerkton.com/";
    } else if (type === HelpDeskType.CADGettingStarted) {
      link = "https://support.kerkton.com/";
    } else if (type === HelpDeskType.MDTGettingStarted) {
      link = "https://support.kerkton.com/";
    } else if (type === HelpDeskType.AdminGettingStarted) {
      link = "https://support.kerkton.com/";
    }

    if ((window as any).isUWPApp || (window as any).isIOSApp || (window as any).isAndroidApp || (window as any).isWindowsAppSdkApp) {
      (window as any).notifyApp("externalLink", [link]);
    } else {
      window.open(link!, "_blank");
    }
  }

  static menuGettingStartedClick(_e: any) {
    (window as any).showHomePage();
  }

  static menuHelpDeskClick(_e: any) {
    App.goToHelpDesk((window as any).HelpDeskType.Home);
  }

  static preloadAudio() {
    const authTokenObj = getAuthObject();
    if (!hasValue(authTokenObj)) return;

    const audioToPreload = [
      { appVar: "sndFire", src: "/Content/Customize/Audio/PoliceDispatch.mp3" },
      { appVar: "sndPanic", src: "/Content/Customize/Audio/PoliceDispatch.mp3" },
      { appVar: "sndPanic2", src: "/Content/Customize/Audio/PoliceDispatch3.mp3" },
      { appVar: "sndCCUpdate", src: "/Content/Customize/Audio/mdtUpdateTone.wav" },
      { appVar: "sndChat", src: "/Content/Customize/Audio/chat.wav" },
    ];
    for (let audio of audioToPreload) {
      if (!hasValue((App as any)[audio.appVar])) {
        (App as any)[audio.appVar] = document.createElement("audio");
        (App as any)[audio.appVar].setAttribute("src", audio.src);
      }
    }
    App.sndPanic!.addEventListener('ended', function (this: HTMLAudioElement) {
      this.currentTime = 0;
      App.sndPanic2!.addEventListener('ended', function (this: HTMLAudioElement) {
        this.currentTime = 0;
        this.play().catch(() => {});
      }, false);
      App.sndPanic2!.play().catch(() => {});
    }, false);
  }

  static stopSndPanic() {
    if (hasValue(App.sndPanic)) {
      App.sndPanic.pause();
      App.sndPanic.currentTime = 0;
    }
    if (hasValue(App.sndPanic2)) {
      App.sndPanic2.pause();
      App.sndPanic2.currentTime = 0;
    }
  }

  static initPubNubAndPreloadAudio() {
    this.preloadAudio();
    this.initPubNub();
  }

  static updatePubNubTokenFromAuthObject() {
    const authObj = getAuthObject();
    if (hasValue(authObj)) {
      initAzureWebPubSub();
    }
  }

  static initPubNub(callback?: () => void) {
    initAzureWebPubSub(callback);
  }

  static startPageAppCue() {
    (window as any)["appCuesStart"]();
  }

  static signOut() {
    (window as any).OrnaReact.navigate.toSignOut();
  }

  /**
   * Check the session and sign out only on a definitive "not logged in"
   * (confirmed by one immediate retry). An "unknown" result — the server
   * couldn't be reached or answered with an error — leaves the user signed
   * in; the next periodic check or a later 401 will settle it.
   */
  static isLoggedIn(callback?: (loggedIn: boolean) => void, confirm: boolean = true,
                    trigger: string = 'unspecified', firstResult?: SessionCheckResult) {
    (window as any).api.isLoggedIn((result: SessionCheckResult) => {
      if (result.state === 'yes') {
        if (firstResult) recordSessionCheck('recovered', trigger, firstResult, result);
        noteSessionCheckYes();
        if (callback !== undefined) callback(true);
      } else if (result.state === 'unknown') {
        recordSessionCheck('skipped-signout', trigger, firstResult ?? result, firstResult ? result : undefined);
        if (callback !== undefined) callback(true);
      } else if (confirm) {
        App.isLoggedIn(callback, false, trigger, result);
      } else {
        recordSessionCheck('signout', trigger, firstResult ?? result, result);
        App.signOut();
        if (callback !== undefined) callback(false);
      }
    });
  }

  static getCurrentFrontendVersion(): number {
    return App._currentFrontendVersion!;
  }

  static setCurrentFrontendVersion(latestVersion: number) {
    App._currentFrontendVersion = latestVersion;
  }

  static latestFrontendVersionCallback(callback: any) {
    latestFrontendVersionCallback(callback);
  }

  static latestBackendVersionCallback(callback: any) {
    latestBackendVersionCallback(callback);
  }

  static newFrontendVersionAvailableCallback(callback: any) {
    newFrontendVersionAvailableCallback(callback);
  }

  static signOutCleanup() {
    localStorage.removeItem('LastRoute');
    removeModuleTypeAndAvailableSubmodules();
    removeAuthObject();
    App.removePubNub();
    (window as any).CompanyChat.chatCleanup();
    (window as any).mapCleanup();
    App.menuCleanup();
    (window as any).Codes.cleanUp();
    (window as any).stopPanicSounds();
    (window as any)._currentCallCardLoadedCallback = null;
    App.clearCustomLocationListCache();
    (window as any).clearPolygonOverlayCache();
    (window as any).clearMapOverlayKmlCache();
    (window as any).cleanUpCaseReportsListScreen();
    App.hasMdtModuleEntryBeenConfirmed = false;
    (window as any).notifyApp("logout");
  }

  static onWindowResize() {
    // InProgress fullscreen grid resizing is handled by the React component
  }

  static showSelectModuleScreen(): void {
    const availableModules: Array<string> = getAvailableModules() ?? [];
    if (hasValue(availableModules) && availableModules.length === 1) {
      let availableModule: string = availableModules[0].toLowerCase();
      (window as any).api.selectModule(availableModule, (result: any) => {
        if (availableModule === "payment") {
          (window as any).OrnaReact.navigate.toPayment();
        } else {
          (window as any).handleMdtModuleSelection(result, () => {
            (window as any).selectModuleCallback(result);
            (window as any).OrnaReact.navigate.toModuleSelected((result.ModuleType || '').toUpperCase());
          });
        }
        return;
      }, () => { });
    } else {
      (window as any).initSelectModuleScreen();
    }
  }

  static createRoutes() {
    (window as any).newVersionCheck = (window as any).OrnaReact ?
      () => newVersionCheck() :
      () => {};
  }

  static uploadFromFileInput(
    fileInput: HTMLInputElement,
    callbacks: {
      successCallback?: (idx: number, attachment: any) => void,
      updateAttachmentInfo?: (idx: number, attachmentInfo: any) => void,
      updateAttachment?: (idx: number, attachment: any) => void
    },
    typeName: string | null = null
  ): void {
    const fileList = fileInput.files;
    if (!fileList || fileList.length === 0) return;

    const files: File[] = Array.from(fileList);
    fileInput.value = '';

    const Attachment = (window as any).Attachment;
    const AttachmentInfo = (window as any).AttachmentInfo;
    const attachmentList: any[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const attachment = new Attachment();
      const lastDotIndex = file.name.lastIndexOf('.');
      attachment.title = lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name;
      attachment.filename = file.name;
      if (hasStringValue(typeName!)) {
        attachment.typeName = typeName;
      }
      attachmentList.push(attachment);
    }

    App.uploadStarted();
    App.uploadProgress(1);
    App.initBlobUploadProgressControl();

    (window as any).api.saveAttachment(attachmentList, (result: any) => {
      App.uploadProgress(1);

      for (let i = 0; i < result.azureStorageBlobInfoList.length; i++) {
        (window as any).load.showLoadingScreen();
        App.blobUploadStarted();

        const file = files[i];
        const attachmentInfo = new AttachmentInfo();
        const blobInfo = result.azureStorageBlobInfoList[i];

        attachmentInfo.id = blobInfo.attachmentId;
        attachmentInfo.activationKey = blobInfo.activationKey;

        if (callbacks && callbacks.updateAttachmentInfo) {
          callbacks.updateAttachmentInfo(i, attachmentInfo);
        }

        const sasReadToken: string = blobInfo.sasReadToken;
        const sasWriteToken: string = blobInfo.sasWriteToken;
        const host: string = blobInfo.host;
        const containerName: string = blobInfo.containerName;
        const blobName: string = blobInfo.blobName;
        const attachment = attachmentList[i];

        attachment.id = blobInfo.attachmentId;
        attachment.signedURL = `${host}/${containerName}/${blobName}?${sasReadToken}`;

        if (callbacks && callbacks.updateAttachment) {
          callbacks.updateAttachment(i, attachment);
        }

        const customBlockSize = file.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;

        uploadBlockBlob(host, sasWriteToken, containerName, blobName, file, {
          blockSize: customBlockSize,
          onProgress: (percent) => {
            App.setBlobUploadProgress(percent);
            App.uploadProgress(percent);
          },
        }).then(() => {
          App.uploadCompleted();
          App.showPopupNotification("File uploaded", "success");
          if (callbacks && callbacks.successCallback) {
            callbacks.successCallback(i, attachment);
          }
          App.blobUploadCompleted();
          (window as any).load.hideLoadingScreen();
        }).catch(() => {
          App.uploadCompleted();
          App.showPopupNotification("File failed to upload", "error");
          App.blobUploadCompleted();
          (window as any).load.hideLoadingScreen();
        });
      }
    });
  }

  static setModuleTypeFromURL(sectionName: string) {
    if (sectionName === null || sectionName === undefined) {
      App.moduleTypeFromURL = null!;
      return;
    }
    const moduleId = sectionName.toLowerCase();
    if (moduleId !== null && moduleId !== undefined) {
      App.moduleTypeFromURL = moduleId;
    } else {
      App.moduleTypeFromURL = null!;
    }
  }

  static getModuleTypeFromURL() {
    return App.moduleTypeFromURL;
  }

  static isViewingMDTPage() {
    return App.getModuleTypeFromURL() === 'mdt' && currentUserHasPermission(AccessAuthorities.ROLE_MDT);
  }

  static isViewingCADPage() {
    return App.getModuleTypeFromURL() === 'cad' && currentUserHasPermission(AccessAuthorities.ROLE_CAD);
  }

  static isViewingAdminPage() {
    return App.getModuleTypeFromURL() === 'admin' && currentUserHasPermission(AccessAuthorities.ROLE_ADMIN);
  }

  static getParameterByName(name: any, url: any) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, "\\$&");
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  static getParam(name: any, url: any) {
    if (url === null) { url = window.location.href; }
    name = name.replace(/[\[\]]/g, "\\$&");
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  static NtoS(value: any) {
    if (value === undefined || value === null) return "";
    else return value;
  }

  static IsNOU(value: any) {
    if (value == null) return true;
    else return false;
  }

  static EmptyToNull(value: string) {
    if (value === "") return null;
    else return value;
  }

  static showVersionUpdateDialog() {
    showVersionUpdateDialog();
  }

  static preloadCompanyLogo() {
    const homeSectionLogoBGStyleFirstPart = `.homeSectionLogoBG::before {content:"";position:absolute;top:0px;left:7px;width:calc(100% - 14px);height:calc(100% - 20px);z-index:99;background-image:`;
    const homeSectionLogoBGStyleSecondPart = `;background-size:contain;background-repeat:no-repeat;background-position:center;opacity:0.1;pointer-events:none;border-radius:4px;}`;

    document.querySelectorAll('style[data-company-logo]').forEach(el => el.remove());

    const appendStyle = function (css: string) {
      const style = document.createElement('style');
      style.setAttribute('data-company-logo', '');
      style.textContent = css;
      document.head.appendChild(style);
    };

    (window as any).api.getHome((result: any) => {
      if (result && result.companyLogo && result.companyLogo.signedURL) {
        const logoURL = result.companyLogo.signedURL;
        appendStyle(`body::after{position:absolute;width:0;height:0;overflow:hidden;z-index:-1;content:url("${logoURL}")}`);
        appendStyle(`${homeSectionLogoBGStyleFirstPart}url("${logoURL}")${homeSectionLogoBGStyleSecondPart}`);
      } else {
        appendStyle(`${homeSectionLogoBGStyleFirstPart}none${homeSectionLogoBGStyleSecondPart}`);
      }
    }, (_result: any) => {
      appendStyle(`${homeSectionLogoBGStyleFirstPart}none${homeSectionLogoBGStyleSecondPart}`);
    });
  }
}

// ── Guid class ────────────────────────────────────────────────────

class Guid {
  static newGuid(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      var r = Math.random() * 16 | 0, v = c == "x" ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

// ── selectModuleCallback ──────────────────────────────────────────

function selectModuleCallback(result: any) {
  (window as any).updatePanicButtonState(result.isPanicButtonEnabled);
  localStorage.setItem("IsShowingAllNatureOfCallOptionsOnSelfInitiate", (result.isShowingAllNatureOfCallOptionsOnSelfInitiate === true).toString());
  localStorage.setItem('IsStatusUpdateEnabled', result.isStatusUpdateEnabled ? 'true' : 'false');
  localStorage.setItem('IsStatusUpdateOnTourStopsEnabled', result.isStatusUpdateOnTourStopsEnabled ? 'true' : 'false');
  localStorage.setItem('IsStatusUpdateForDispatchersEnabled', result.isStatusUpdateForDispatchersEnabled ? 'true' : 'false');
  let moduleTypeUpperCase = (result.ModuleType || '').toUpperCase();
  App.menuCleanup();
  App.setMenu(moduleTypeUpperCase);
  App.setModuleTypeFromURL(moduleTypeUpperCase);
  if (typeof (window as any).trackUser === 'function') {
    (window as any).trackUser();
  }
  (window as any).notifyApp("geoTrack", [moduleTypeUpperCase, (window as any).api.trackUserEndpoint]);

  if (moduleTypeUpperCase === "MDT") {
    (window as any).addMDTActiveCallCard(result.callCardId, true);
    if (result.isPanicButtonEnabled) {
      (window as any).setMDTPanicButtonBlinkingFromServerState();
    }
  } else if (moduleTypeUpperCase === "CAD") {
    if (result.isPanicButtonEnabled) {
      (window as any).setCADPanicButtonBlinkStateFromServer();
    }
  }

  // Reauthorize realtime after module selection so the JWT gets
  // the correct permissions (e.g., ROLE_VIEW_MESSAGING for chat)
  reauthorizeAndReconnectRealtime();

  // One-time feature intros. Inline intros (e.g. the User Management single-user
  // nudge) read the pending keys from this store at render time; the modal flow
  // below consumes the list directly.
  setPendingFeatureIntros(result.pendingFeatureIntros);

  // Surface the first pending intro that has a client registry entry. On enable,
  // re-run module select so newly granted submodules/menus appear without a re-login.
  showPendingFeatureIntro(result.pendingFeatureIntros, () => {
    const refreshModule: string = (result.ModuleType || '').toLowerCase();
    if (!refreshModule) return;
    (window as any).api.selectModule(refreshModule, (refreshed: any) => {
      const refreshedType: string = (refreshed.ModuleType || '').toUpperCase();
      setPendingFeatureIntros(refreshed.pendingFeatureIntros);
      App.menuCleanup();
      App.setMenu(refreshedType);
    }, () => { });
  });
}

// ── Keep-alive ────────────────────────────────────────────────────

let _lastAuthTokenRefresh: any = moment();

export function updateLastAuthTokenRefresh() {
  _lastAuthTokenRefresh = moment().utc();
}

function getLastAuthTokenRefresh(): any {
  return _lastAuthTokenRefresh;
}

function doKeepAliveCheck() {
  const curTime = moment().utc();
  const keepAliveThreshholdMins = 15;
  const keepAliveThreshholdMilliSeconds = keepAliveThreshholdMins * 1000 * 60;
  if (curTime.diff(getLastAuthTokenRefresh()) > keepAliveThreshholdMilliSeconds) {
    (window as any).api.keepAlive();
  }
}

document.addEventListener("mouseup", function () { doKeepAliveCheck(); resetPeriodicVersionCheckTimer(); });
document.addEventListener("keyup", function () { doKeepAliveCheck(); resetPeriodicVersionCheckTimer(); });
document.addEventListener("touchend", function () { doKeepAliveCheck(); resetPeriodicVersionCheckTimer(); });
resetPeriodicVersionCheckTimer(); // Start the first inactivity timer

// ── MDT officer call time ─────────────────────────────────────────

function updateMdtOfficerCallTime(typeInUrl: string, typeInData: string, successMessage: string) {
  const activeCallCardId: number = (window as any).getMDTActiveCallCardId();
  if (!hasValue(activeCallCardId) || isNaN(activeCallCardId)) return;

  if (currentUserHasPermission(AccessAuthorities.ROLE_MDT_UPDATE_RESPONSE_TIMES)) {
    let data: any = {};
    data[typeInData] = (window as any).currentISODateTime((window as any).DATETIME_FORMAT_SERVER_UTC);

    (window as any).setCallCardRealtimeEnabled(false);
    (window as any).api.mdtActions(typeInUrl, activeCallCardId.toString(), JSON.stringify(data), (successResult: any) => {
      if (typeInUrl === "cleared") {
        (window as any).removeMDTActiveCallCard(activeCallCardId);
      }
      if ((window as any).isCallCardScreenLoaded()) {
        (window as any).refreshPage();
        (window as any).setShowingRecentOfficerAssignedDialogOnPageLoaded(true);
      }
    }, (_errorResult: any) => {
      (window as any).setCallCardRealtimeEnabled(true);
    });
  } else {
    (window as any).setCallCardRealtimeEnabled(false);
    (window as any).api.mdtEnrouteOnSceneOrClearedNow(typeInUrl, activeCallCardId.toString(), (successResult: any) => {
      if (typeInUrl === "cleared") {
        (window as any).removeMDTActiveCallCard(activeCallCardId);
      }
      if ((window as any).isCallCardScreenLoaded()) {
        (window as any).refreshPage();
        (window as any).setShowingRecentOfficerAssignedDialogOnPageLoaded(true);
      }
    }, (_errorResult: any) => {
      (window as any).setCallCardRealtimeEnabled(true);
    });
  }
}

// ── notifyApp ─────────────────────────────────────────────────────

function notifyApp(type: string, data: Array<string> | null = null) {
  let version = 1;
  let notifyMessage = { version: version, type: type, data: data };
  if ((window as any).isUWPApp) {
    (window as any).external.notify(JSON.stringify(notifyMessage));
  } else if ((window as any).isIOSApp) {
    (window as any).webkit.messageHandlers.callbackHandler.postMessage(notifyMessage);
  } else if ((window as any).isAndroidApp) {
    (window as any).Android.notifyApp(JSON.stringify(notifyMessage));
  } else if ((window as any).isWindowsAppSdkApp) {
    (window as any).chrome.webview.postMessage(notifyMessage);
  }
}

// ── Realtime reauthorize and reconnect ─────────────────────────────

function reauthorizeAndReconnectRealtime() {
  if (!getAuthObject()) return;
  // Stop current connection, fetch fresh credentials, and reconnect
  stopAzureWebPubSub();
  (window as any).api.reauthorizeRealtime((result: any) => {
    const authObj = getAuthObject();
    if (authObj && result) {
      if (result.realtimeToken) authObj.realtimeToken = result.realtimeToken;
      if (result.realtimeUrl) authObj.realtimeUrl = result.realtimeUrl;
      (window as any).setAuthObject(authObj);
    }
    initAzureWebPubSub();
  });
}

// ── addressValidationErrorMsg ─────────────────────────────────────

const addressValidationErrorMsg: string = "Error validating location. Please re-enter the location address.";

// ── _currentCallCardLoadedCallback ────────────────────────────────

let _currentCallCardLoadedCallback: Function | null = null;

// ── _userUploadActionURL ──────────────────────────────────────────

let _userUploadActionURL: string = "";

// ── doesNeedToUpdateWindowsAppSdkApp ──────────────────────────────

let doesNeedToUpdateWindowsAppSdkApp = true;

// ── Register everything on window ─────────────────────────────────

(window as any).App = App;
(window as any).Guid = Guid;
(window as any).notifyApp = notifyApp;
(window as any).selectModuleCallback = selectModuleCallback;
(window as any).updateLastAuthTokenRefresh = updateLastAuthTokenRefresh;
(window as any).getLastAuthTokenRefresh = getLastAuthTokenRefresh;
(window as any).doKeepAliveCheck = doKeepAliveCheck;
(window as any).updateMdtOfficerCallTime = updateMdtOfficerCallTime;
(window as any).addressValidationErrorMsg = addressValidationErrorMsg;
Object.defineProperty(window, '_currentCallCardLoadedCallback', {
  get: () => _currentCallCardLoadedCallback,
  set: (v) => { _currentCallCardLoadedCallback = v; },
  configurable: true,
});
(window as any)._userUploadActionURL = _userUploadActionURL;
(window as any).doesNeedToUpdateWindowsAppSdkApp = doesNeedToUpdateWindowsAppSdkApp;

// Toast / confirm / prompt bridges (previously in Vue-A0005-Toast.ts, Vue-A0010-KstConfirm.ts, Vue-A0010-KstPrompt.ts)
(window as any)._kstToastAdd = function (message: string, type: string, _minimal?: boolean): void {
  (window as any).OrnaReact.showToast(message, type);
};

(window as any).kstConfirm = function (options: {
  title?: string, message?: string, messageHtml?: string,
  confirmText?: string, cancelText?: string, confirmClass?: string,
  showCancel?: boolean, theme?: string
}): Promise<boolean> {
  return (window as any).OrnaReact.confirm({
    title: options.title || 'Confirm Action',
    message: options.message || '',
    messageHtml: options.messageHtml || '',
    confirmText: options.confirmText || 'Confirm',
    cancelText: options.cancelText || 'Cancel',
    confirmClass: options.confirmClass || 'danger',
    showCancel: options.showCancel !== false,
    theme: options.theme
  });
};

(window as any).kstPrompt = function (options: {
  title?: string, message?: string, placeholder?: string,
  submitText?: string, cancelText?: string, inputType?: string,
  theme?: string, inputName?: string
}): Promise<string | null> {
  const fieldName = options.inputName || 'input';
  return (window as any).OrnaReact.prompt({
    title: options.title || 'Input',
    fields: [{
      name: fieldName,
      label: options.message || '',
      type: options.inputType || 'text',
      placeholder: options.placeholder || '',
    }],
    confirmText: options.submitText || 'Submit',
    cancelText: options.cancelText || 'Cancel',
    theme: options.theme
  }).then(function (result: Record<string, any> | null) {
    return result ? (result[fieldName] ?? null) : null;
  });
};
